import React, { useRef } from 'react';
import { DialogV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { AmountStep } from './AmountStep';
import { MethodStep } from './MethodStep';
import { ConnectBankStep, useIsAllyPromoVisible } from './ConnectBankStep';
import { StatusPayoutDialog } from './TransferStatus';
import { RestrictedStep } from './RestrictedStep';
import type { PayoutAccountInfo, PayoutDialogStep, PayoutStatus, PayoutMethod } from './PayoutDialog.types';

export type { PayoutStatus, PayoutDialogStep };

type PayoutDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  payoutAccountInfo: PayoutAccountInfo;
  dialogStep: PayoutDialogStep;
  amountInMinorUnits: number;
  onAmountChange: (amount: number) => void;
  onContinue: () => void;
  onBack: () => void;
  selectedMethod: PayoutMethod;
  onMethodChange: (method: PayoutMethod) => void;
  onTransfer: () => void;
  loadingTransfer: boolean;
  status: PayoutStatus;
  onConnectBank?: () => void;
  onAmountStepViewed?: () => void;
  onMethodStepViewed?: () => void;
  onStatusDone?: (status: PayoutStatus) => void;
  onBankPromoViewed?: (partner: string) => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

export const PayoutDialog: React.FC<PayoutDialogProps> = ({
  onClose,
  isOpen,
  payoutAccountInfo,
  dialogStep,
  amountInMinorUnits,
  onAmountChange,
  onContinue,
  onBack,
  selectedMethod,
  onMethodChange,
  onTransfer,
  loadingTransfer,
  status,
  onConnectBank,
  onAmountStepViewed,
  onMethodStepViewed,
  onStatusDone,
  onBankPromoViewed,
  onBankPromoInteracted,
}) => {
  // Freeze step/status/method while closing so the parent resetting state mid-animation doesn't flash a different step.
  const frozenStep = useRef(dialogStep);
  const frozenStatus = useRef(status);
  const frozenMethod = useRef(selectedMethod);
  if (isOpen) {
    frozenStep.current = dialogStep;
    frozenStatus.current = status;
    frozenMethod.current = selectedMethod;
  }
  const visibleStep = frozenStep.current;
  const visibleStatus = frozenStatus.current;
  const visibleMethod = frozenMethod.current;

  const isAllyPromoVisible = useIsAllyPromoVisible(payoutAccountInfo);
  const isAllyPromoStep = visibleStep === 'connectBank' && isAllyPromoVisible;

  return (
    <DialogV2
      onClose={onClose}
      isOpen={isOpen}
      size="md"
      overrides={{
        Content: {
          props: {
            width: isAllyPromoStep ? pxToRem(500) : undefined,
            maxWidth: isAllyPromoStep ? '100%' : pxToRem(460),
            borderRadius: pxToRem(24),
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 18px 40px -12px rgba(0,0,0,0.18)',
          },
        },
        Body: { props: { padding: 0, display: 'flex', flexDirection: 'column' } },
      }}
    >
      <DialogV2.CloseButton />
      <DialogV2.Body>
        <Flex
          flexDirection="column"
          flex={1}
          minHeight={pxToRem(360)}
          padding={[6, null, 0]}
        >
          {visibleStep === 'connectBank' && onConnectBank && (
            <ConnectBankStep
              payoutAccountInfo={payoutAccountInfo}
              onConnectBank={onConnectBank}
              onBankPromoViewed={onBankPromoViewed}
              onBankPromoInteracted={onBankPromoInteracted}
            />
          )}
          {visibleStep === 'amount' && (
            <AmountStep
              payoutAccountInfo={payoutAccountInfo}
              amountInMinorUnits={amountInMinorUnits}
              onAmountChange={onAmountChange}
              onContinue={onContinue}
              onClose={onClose}
              onPageViewed={onAmountStepViewed}
            />
          )}
          {visibleStep === 'method' && (
            <MethodStep
              payoutAccountInfo={payoutAccountInfo}
              amountInMinorUnits={amountInMinorUnits}
              selectedMethod={selectedMethod}
              onMethodChange={onMethodChange}
              onTransfer={onTransfer}
              loadingTransfer={loadingTransfer}
              onBack={onBack}
              onPageViewed={onMethodStepViewed}
            />
          )}
          {(visibleStep === 'confirmation' || visibleStatus === 'error') && (
            <StatusPayoutDialog
              onClose={onClose}
              status={visibleStatus}
              selectedMethod={visibleMethod}
              onDone={onStatusDone}
            />
          )}
          {visibleStep === 'restricted' && payoutAccountInfo.nextWithdrawalAvailableAt && (
            <RestrictedStep
              nextWithdrawalAvailableAtMs={payoutAccountInfo.nextWithdrawalAvailableAt.milliseconds}
              onDone={onClose}
            />
          )}
        </Flex>
      </DialogV2.Body>
    </DialogV2>
  );
};
